import React, { useState, useEffect } from 'react';
import { Card, Space, Button } from 'antd';
import { TablePaginationConfig } from 'antd/lib/table';
import { useVideoCallRecordings } from '@hooks/videoCallRecordings/queries';
import { useSyncVideoCallRecordings } from '@hooks/videoCallRecordings/mutations';
import { CourseSelect } from '@components/courses/CourseSelect';
import { FilterValue, SorterResult } from 'antd/lib/table/interface';
import { VideoCallRecordingsTable, VideoCallRecordingModal } from '@components/videoCallRecordings';

export const VideoCallRecordingsPage: React.FC = () => {
  const [courseId, setCourseId] = useState<number>();
  const [selectedVideoCallRecording, setSelectedVideoCallRecording] =
    useState<VideoCallRecording>();
  const [sorter, setSorter] = useState<SorterResult<VideoCallRecording>>({
    field: 'createdAt',
    order: 'descend',
  });
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const { data, isLoading } = useVideoCallRecordings({
    page: pagination.current || 1,
    pageSize: pagination.pageSize || 10,
    courseId,
    orderField: (sorter?.field as string) || 'createdAt',
    orderDirection: sorter?.order === 'ascend' ? 'ASC' : 'DESC',
  });

  const { mutate: syncVideoCallRecordings, isLoading: isSyncing } = useSyncVideoCallRecordings();

  const handleReset = () => {
    setSorter({
      field: 'createdAt',
      order: 'descend',
    });
    setPagination({
      current: 1,
      pageSize: 10,
      total: 0,
    });
    setCourseId(undefined);
  };

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<VideoCallRecording> | SorterResult<VideoCallRecording>[]
  ) => {
    setPagination(pagination);
    if (Array.isArray(sorter)) {
      setSorter(sorter[0]);
    } else {
      setSorter(sorter);
    }
  };

  useEffect(() => {
    setPagination({
      ...pagination,
      total: data?.total || 0,
    });
  }, [data]);

  return (
    <Card
      title="Video Call Recordings"
      actions={[
        <Button
          key="sync"
          type="primary"
          onClick={() => syncVideoCallRecordings()}
          loading={isSyncing}
          disabled={isSyncing}
        >
          Sync
        </Button>,
      ]}
    >
      <Space direction="horizontal" style={{ width: '100%', marginBottom: 10 }}>
        <CourseSelect
          onChange={(value) => setCourseId(value)}
          value={courseId || undefined}
          style={{ width: 300 }}
        />
        <Button onClick={handleReset}>Reset sorting & filtering</Button>
      </Space>
      <VideoCallRecordingsTable
        data={data?.data || []}
        loading={isLoading}
        onChange={handleTableChange}
        pagination={pagination}
        sorter={sorter}
        onSelect={setSelectedVideoCallRecording}
      />
      {selectedVideoCallRecording && (
        <VideoCallRecordingModal
          open={true}
          onClose={() => setSelectedVideoCallRecording(undefined)}
          videoCallRecording={selectedVideoCallRecording}
        />
      )}
    </Card>
  );
};
